import { openDB, type IDBPDatabase } from 'idb';

/**
 * Mídias de checklist ainda NÃO enviadas, guardadas no aparelho (oficina/at-136).
 *
 * O texto do rascunho vai para o banco; os arquivos não cabem lá (a recepção anexa lotes de 100+
 * fotos). Ficam no IndexedDB deste navegador para sobreviverem ao recarregamento que a versão nova
 * do sistema faz sozinha — o mesmo mecanismo das fotos da visita do inspetor (at-114).
 *
 * ⚠️ É do APARELHO: trocar de computador ou limpar os dados do navegador perde estes arquivos (o
 * texto do rascunho continua no servidor).
 */

const NOME_DO_BANCO = 'soac-rascunhos';
const VERSAO = 1;
const STORE = 'midias';

export type OrigemDaMidia = 'ANGULO' | 'SECAO' | 'SAIDA';

export interface MidiaGuardada {
  id: string;
  /** A chave do rascunho a que a mídia pertence (`checklist:<carId>:<tipo>`). */
  chave: string;
  origem: OrigemDaMidia;
  /** Ângulo (slot) ou nome da seção personalizada. Vazio nas imagens de saída. */
  grupo: string;
  nome: string;
  tipo: string;
  blob: Blob;
  /** Ordem de seleção, para a tela voltar como estava. */
  ordem: number;
}

let promessa: Promise<IDBPDatabase> | null = null;

const abrir = (): Promise<IDBPDatabase> => {
  if (!promessa) {
    promessa = openDB(NOME_DO_BANCO, VERSAO, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          const s = db.createObjectStore(STORE, { keyPath: 'id' });
          s.createIndex('chave', 'chave');
        }
      },
    }).catch((erro) => {
      // Uma falha não condena as tentativas seguintes (outra aba, outro momento).
      promessa = null;
      throw erro;
    });
  }
  return promessa;
};

export async function guardarMidia(midia: MidiaGuardada): Promise<void> {
  const db = await abrir();
  await db.put(STORE, midia);
}

export async function removerMidia(id: string): Promise<void> {
  const db = await abrir();
  await db.delete(STORE, id);
}

export async function lerMidias(chave: string): Promise<MidiaGuardada[]> {
  const db = await abrir();
  const todas = (await db.getAllFromIndex(STORE, 'chave', chave)) as MidiaGuardada[];
  return todas.sort((a, b) => a.ordem - b.ordem);
}

export async function limparMidias(chave: string): Promise<void> {
  const db = await abrir();
  const tx = db.transaction(STORE, 'readwrite');
  const ids = await tx.store.index('chave').getAllKeys(chave);
  await Promise.all(ids.map((id) => tx.store.delete(id)));
  await tx.done;
}

/** Só para teste. */
export function __fecharParaTeste(): void {
  promessa = null;
}
